import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { ApiError, getMe } from "../lib/api";
import { errorMessage } from "../lib/errors";

/** 只允许站内相对路径，防开放重定向。 */
function safeNext(raw: string | null): string {
  if (!raw) return "/";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/";
  if (raw.startsWith("/login")) return "/";
  return raw;
}

function GoogleMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" style={{ width: 14, height: 14 }}>
      <path
        fill="#4285F4"
        d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2a7 7 0 0 1-6.6-4.8H1.4v3.1A12 12 0 0 0 12 24Z"
      />
      <path fill="#FBBC05" d="M5.4 14.5a7.2 7.2 0 0 1 0-5V6.4H1.4a12 12 0 0 0 0 11.2l4-3.1Z" />
      <path
        fill="#EA4335"
        d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.4 6.4l4 3.1A7 7 0 0 1 12 4.8Z"
      />
    </svg>
  );
}

/**
 * 登录页：居中卡片 + 大标题 + 琥珀主按钮。
 * 探测登录态的三种结果（进行中 / 未登录 / 检查失败 / 已登录）都留在卡片里。
 */
export function LoginPage() {
  const [searchParams] = useSearchParams();
  const next = safeNext(searchParams.get("next"));

  // /login 自己探测登录态：401 不触发全局跳转，否则会自己跳自己。
  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: () => getMe({ redirectOnUnauthorized: false }),
    retry: false,
  });

  const loggedOut =
    meQuery.isError &&
    meQuery.error instanceof ApiError &&
    (meQuery.error.code === "unauthorized" || meQuery.error.code === "session_expired");
  const checkFailed = meQuery.isError && !loggedOut;
  const googleHref = `/api/auth/google/start?next=${encodeURIComponent(next)}`;

  return (
    <main
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "56px 20px",
      }}
    >
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 26,
            justifyContent: "center",
          }}
        >
          <span className="dp-brand-mark" aria-hidden="true">
            D
          </span>
          <span
            style={{
              fontFamily: "var(--sans)",
              fontSize: 18,
              letterSpacing: ".08em",
              color: "var(--ink)",
            }}
          >
            Drama Pie
          </span>
        </div>

        <section
          aria-labelledby="login-heading"
          style={{
            border: "1px solid var(--line)",
            borderRadius: 12,
            background: "linear-gradient(180deg, var(--wash-1), transparent), var(--panel)",
            padding: "30px 28px 24px",
            boxShadow: "var(--shadow-card)",
          }}
        >
          <p className="dp-ep-eyebrow" style={{ justifyContent: "center" }}>
            剪辑室 · 入口
          </p>
          <h1
            id="login-heading"
            style={{
              margin: "12px 0 8px",
              textAlign: "center",
              fontFamily: "var(--sans)",
              fontSize: 32,
              fontWeight: 500,
              lineHeight: 1.2,
              letterSpacing: ".03em",
              color: "var(--ink)",
            }}
          >
            把剧本变成可拍的分镜
          </h1>
          <p
            style={{
              margin: "0 auto 20px",
              maxWidth: 320,
              textAlign: "center",
              fontSize: 13,
              lineHeight: 1.75,
              color: "var(--ink-2)",
            }}
          >
            按场次组织故事板，逐镜确认首帧、台词与检查项。登录后回到你上次的项目。
          </p>

          <a
            href={googleHref}
            className="dp-btn primary"
            style={{ width: "100%", padding: "9px 12px", fontSize: 13 }}
          >
            <GoogleMark />
            使用 Google 登录
          </a>

          {/* P1-W1 域级 SSO：登录的是 Drama Pie 账号，三个站点共用同一份登录态。 */}
          <p
            style={{
              margin: "10px 0 0",
              textAlign: "center",
              fontSize: 12.5,
              lineHeight: 1.6,
              color: "var(--ink-3)",
            }}
          >
            Drama Pie 账号，Studio / Plan / Canvas 通用。
          </p>

          <div
            style={{
              marginTop: 18,
              paddingTop: 14,
              borderTop: "1px solid var(--line)",
              minHeight: 56,
              fontSize: 12.5,
            }}
          >
            {meQuery.isPending ? (
              <p role="status" aria-live="polite" style={{ margin: 0, color: "var(--ink-3)" }}>
                正在检查登录状态…
              </p>
            ) : loggedOut ? (
              <p style={{ margin: 0, color: "var(--ink-3)" }}>
                还没有登录。登录后回到你上次的项目。
              </p>
            ) : checkFailed ? (
              <div role="alert" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <p style={{ margin: 0, color: "var(--red)" }}>
                  登录状态检查失败：{errorMessage(meQuery.error)}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    void meQuery.refetch();
                  }}
                  className="dp-btn sm"
                  style={{ alignSelf: "flex-start" }}
                >
                  重新检查
                </button>
              </div>
            ) : meQuery.data ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <p style={{ margin: 0, color: "var(--ink)" }}>
                  已登录为{" "}
                  <span style={{ fontFamily: "var(--mono)", fontSize: 12.5 }}>
                    {meQuery.data.displayName ?? meQuery.data.email}
                  </span>
                </p>
                <Link
                  to={next}
                  className="dp-btn sm primary"
                  style={{ alignSelf: "flex-start" }}
                >
                  进入项目列表 <span aria-hidden="true">→</span>
                </Link>
              </div>
            ) : null}
          </div>

          <p
            style={{
              margin: "10px 0 0",
              fontFamily: "var(--mono)",
              fontSize: 12,
              lineHeight: 1.7,
              color: "var(--ink-4)",
            }}
          >
            登录态保存在 HttpOnly Cookie 里，有效期 30 天。
          </p>
        </section>

        <p
          style={{
            margin: "18px 0 0",
            textAlign: "center",
            fontFamily: "var(--mono)",
            fontSize: 12,
            letterSpacing: ".08em",
            color: "var(--ink-4)",
          }}
        >
          9:16 竖屏 · 按场次分组 · 逐镜确认
        </p>
      </div>
    </main>
  );
}
